<script setup>
import IconInformation from '@/components/icons/IconInformation.vue'
import PriceDisplay from '@/components/Shopify/PriceDisplay.vue'
import { useShopify } from '@/stores/shopify'
import { useStateStore } from '@/stores/state.store'
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const props = defineProps({
  productId: {
    type: String,
    default: null
  },
  headline: {
    type: String,
    default: ''
  },
  infoIcon: {
    type: Boolean,
    default: false
  },
  showInfluencer: {
    type: Boolean,
    default: false
  },
  onPage: {
    type: Boolean,
    default: false
  }
})

const state = useStateStore()
const shopify = useShopify()

const _loading = ref(true)
const _influencers = ref([])
const _products = ref([])
const scrollContainer = useTemplateRef('scrollContainer')

const fetchInfluencers = async () => {
  try {
    const response = await shopify.fetchInfluencers()
    _influencers.value = response.map((edge) => edge.node)
  } catch (error) {
    console.error('Failed to fetch influencers:', error)
  }
}

const matchProductsWithInfluencers = (products) => {
  // Create a Map for quick lookup
  const influencerMap = new Map(
    _influencers.value.flatMap((influencer) =>
      influencer.products.edges.map((edge) => [edge.node.id.split('/').pop(), influencer])
    )
  )

  return products.map((product) => {
    const productId = product.id.split('/').pop()
    const matchedInfluencer = influencerMap.get(productId)

    return {
      ...product,
      influencer: matchedInfluencer
        ? {
            id: matchedInfluencer.id,
            title: matchedInfluencer.title,
            image: matchedInfluencer.image?.url || null
          }
        : null
    }
  })
}

const initializeProducts = async () => {
  if (props.productId == null) {
    return;
  }

  _loading.value = true
  shopify.fetchRelatedProducts(props.productId)
    .then((response) => {
      if (props.showInfluencer) {
        fetchInfluencers().then(() => {
          _products.value = matchProductsWithInfluencers(response.productRecommendations).splice(0, 6)
        })
      } else {
        _products.value = response.productRecommendations.splice(0, 6);
      }
    })
    .finally(() => {
      _loading.value = false
    });
}

watch(() => [props.productId], initializeProducts, { immediate: true })

function selectProduct(product) {
  if (props.onPage) {
    router.push({ name: 'ProductView', params: { handle: product.handle } })
  } else {
    state.setProduct(product)
  }
}

let drag = ref(false)
let startX = 0
let diffX = 0

const dragEnd = () => {
  if (drag.value) {
    drag.value = false

    let start = 1,
      animate = function () {
        let step = Math.sin(start)
        if (step <= 0) {
          window.cancelAnimationFrame(animate)
        } else {
          scrollContainer.value.scrollLeft += diffX * step
          start -= 0.02
          window.requestAnimationFrame(animate)
        }
      }
    animate()
  }
}

const mouseDown = (event) => {
  startX = event.clientX + scrollContainer.value.scrollLeft
  diffX = 0
  event.preventDefault()
}

const mouseMove = (event) => {
  if (event.buttons === 1 && event.button === 0) {
    diffX = startX - (event.clientX + scrollContainer.value.scrollLeft)
    scrollContainer.value.scrollLeft += diffX
    drag.value = true
  }
}

onMounted(() => {
  scrollContainer.value.addEventListener('mousedown', mouseDown)
  scrollContainer.value.addEventListener('mousemove', mouseMove)
  scrollContainer.value.addEventListener('mouseup', dragEnd);
  scrollContainer.value.addEventListener('mouseleave', dragEnd);
})

onBeforeUnmount(() => {
  scrollContainer.value.removeEventListener('mousedown', mouseDown);
  scrollContainer.value.removeEventListener('mousemove', mouseMove);
  scrollContainer.value.removeEventListener('mouseup', dragEnd);
  scrollContainer.value.removeEventListener('mouseleave', dragEnd);
});
</script>

<template>
  <div class="related-products">
    <div v-if="_products.length > 0 && headline" class="related-products__header">
      <h3>{{ headline }}</h3>
      <template v-if="infoIcon">
        <IconInformation />
      </template>
    </div>
    <div class="related-products__list" ref="scrollContainer">
      <div
        v-for="product in _products"
        :key="product.id"
        class="related-products__item"
        :class="{ disabled: drag }"
        @click="selectProduct(product)"
      >
        <img class="related-products__item-image" :src="product.featuredImage.url" alt="" />
        <!-- <div v-if="product.influencer" class="related-products__influencer">
          <img
            class="related-products__influencer-avatar"
            :src="product.influencer.image"
            :alt="product.influencer.title"
          />
          <span class="related-products__influencer-name">{{ product.influencer.title }}</span>
        </div> -->
        <p class="related-products__item-name">{{ product.title }}</p>
        <PriceDisplay
          :price="product.priceRange.minVariantPrice"
          :font-weight="400"
          :font-size="0.9"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.related-products__item .price {
  position: absolute;
  bottom: -8px;
  right: 0;
  background: #94d4fa;
  color: white;
  padding: 2px 12px;
  border-radius: 15px;
}


.related-products {
  background-color: var(--clr-white);
  width: 100%;
  overflow: hidden;


  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    margin: 16px 0;

    h3 {
      font-size: 16px;
      font-weight: 700;
    }
  }

  &__list {
    display: flex;
    gap: 20px;
    // margin: 16px -20px 0 -20px;
    padding-inline: 24px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 20px;
  }

  &__item {
    width: 120px;
    min-width: 120px;
    color: var(--clr-black);
    text-decoration: none;

    background: white;
    padding: 8px 8px 14px 8px;
    border-radius: 24px;
    position: relative;

    &.disabled {
      pointer-events: none;
    }
  }

  &__item-image {
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 24px;
    margin-bottom: 14px;
  }

  &__item-name {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;

    display: none;
  }

  &__influencer {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__influencer-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }

  &__influencer-name {
    font-size: 14px;
    font-weight: 700;
  }
}
</style>
